import React, { useEffect, useMemo, useState } from 'react';
import { ref, set } from 'firebase/database';
import { toast } from 'react-toastify';
import {
    FiActivity,
    FiAlertCircle,
    FiAlertTriangle,
    FiArrowDown,
    FiArrowUp,
    FiClock,
    FiDownload,
    FiRefreshCw,
    FiUpload,
    FiWifi,
    FiX,
    FiZap,
} from 'react-icons/fi';

import { db } from '../../firebase';
import { countryOfIp } from '../../utils/ipCountry';
import {
    humanizeAge,
    latencyTone,
    packetLossTone,
    toneClasses,
} from '../../utils/deviceData';

// Network tab — connectivity at a glance + on-demand internet speed test.
//
// The Speed Test panel implements the contract from DEVICE-TEST-SPEED.md:
// dashboard writes `run_speedtest` to the command field, the device
// updates `speed_test` with running/success/error/busy. We just listen
// and render — no business logic on this side.

export default function NetworkTab({ mac, device, ipToCountry }) {
    return (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <NetworkSummary device={device} ipToCountry={ipToCountry} />
            <SpeedTestPanel mac={mac} device={device} />
        </div>
    );
}

// ---------------------------------------------------------------------
// Network summary — IP / country / network type / latency / packet loss.
// All read from the live device snapshot. Per DEVICE-DATA.md §6, missing
// numeric fields render as "—" rather than 0.
// ---------------------------------------------------------------------

function NetworkSummary({ device, ipToCountry }) {
    const dd = device?.device_data || {};
    const nq = dd.network_quality || {};

    const country = countryOfIp(ipToCountry, device?.ip_address);

    const latencyT = latencyTone(nq.latency_ms);
    const lossT = packetLossTone(nq.packet_loss_percent);
    const lc = toneClasses(latencyT);
    const pc = toneClasses(lossT);

    return (
        <section className="card-chrome p-5">
            <header className="mb-4">
                <h2 className="text-base font-semibold text-brand-400 inline-flex items-center gap-2">
                    <FiWifi /> Connectivity
                </h2>
            </header>

            <dl className="space-y-2.5 text-sm">
                <SummaryRow label="IP Address" value={device?.ip_address || <Muted>N/A</Muted>} />
                <SummaryRow
                    label="Country"
                    value={country ? (
                        <span className="inline-flex items-center gap-2">
                            {country.country_code && (
                                <img
                                    src={`https://flagcdn.com/24x18/${country.country_code.toLowerCase()}.png`}
                                    alt=""
                                    className="w-4 h-3 object-cover"
                                    onError={(e) => { e.currentTarget.style.display = 'none'; }}
                                />
                            )}
                            {country.country_name || 'Unknown'}
                        </span>
                    ) : <Muted>Unknown</Muted>}
                />
                <SummaryRow label="Network Type" value={dd.network_type || <Muted>—</Muted>} />
                <SummaryRow label="Screen" value={dd.screen_resolution || <Muted>—</Muted>} />

                <SummaryRow
                    label="Latency"
                    value={
                        nq.latency_ms != null ? (
                            <span className={lc.text}>
                                {nq.latency_ms} ms
                            </span>
                        ) : <Muted>—</Muted>
                    }
                />
                <SummaryRow
                    label="Packet Loss"
                    value={
                        nq.packet_loss_percent != null ? (
                            <span className={pc.text}>
                                {nq.packet_loss_percent}%
                            </span>
                        ) : <Muted>—</Muted>
                    }
                />
            </dl>

            <p className="mt-4 pt-3 border-t border-white/[0.06] text-xs text-gray-500">
                Latency is the TCP-handshake RTT to <span className="font-mono">1.1.1.1:443</span>;
                packet loss is the percentage of failed probes out of 5 attempts.
            </p>
        </section>
    );
}

function SummaryRow({ label, value }) {
    return (
        <div className="grid grid-cols-[140px_1fr] items-baseline gap-3">
            <dt className="text-gray-400">{label}</dt>
            <dd className="text-gray-200">{value}</dd>
        </div>
    );
}

function Muted({ children }) {
    return <span className="text-gray-500">{children}</span>;
}

// ---------------------------------------------------------------------
// Speed Test — DEVICE-TEST-SPEED.md states:
//   running → spinner + elapsed. >60s → "Retry" hint (test probably crashed).
//   success → ↓ / ↑ / latency + "tested X ago" + Run again.
//   error   → reason + Retry.
//   busy    → toast on entry; auto-resolves to running then success/error.
// ---------------------------------------------------------------------

const STUCK_THRESHOLD_MS = 60 * 1000;

function SpeedTestPanel({ mac, device }) {
    const result = device?.speed_test || null;
    const status = result?.status || null;

    // Tick once a second while a test is running so the elapsed counter
    // updates. Pure presentational state — no Firebase write.
    const [, setTick] = useState(0);
    useEffect(() => {
        if (status !== 'running') return undefined;
        const id = setInterval(() => setTick((n) => n + 1), 1000);
        return () => clearInterval(id);
    }, [status]);

    const elapsedMs = useMemo(() => {
        if (status !== 'running' || !result?.started_at) return 0;
        return Math.max(0, Date.now() - Number(result.started_at));
    }, [status, result?.started_at, /* re-eval each tick: */ ]);
    const isStuck = elapsedMs > STUCK_THRESHOLD_MS;

    const [pendingTrigger, setPendingTrigger] = useState(false);
    const [confirmOpen, setConfirmOpen] = useState(false);

    // The 20MB-data warning lives in a custom modal (below) rather than
    // window.confirm — DEVICE-TEST-SPEED.md §6 mandates the warning, and
    // the OS dialog looks foreign next to the rest of the dashboard.
    const requestTest = () => setConfirmOpen(true);

    const runTest = async () => {
        setConfirmOpen(false);
        try {
            setPendingTrigger(true);
            await set(ref(db, `mdm/devices/${mac}/command`), 'run_speedtest');
            toast.info('Speed test requested — waiting for the device to start…');
        } catch (err) {
            toast.error(`Failed to send command: ${err.message}`);
        } finally {
            setPendingTrigger(false);
        }
    };

    return (
        <section className="card-chrome p-5">
            <header className="mb-4 flex items-center justify-between">
                <h2 className="text-base font-semibold text-brand-400 inline-flex items-center gap-2">
                    <FiZap /> Speed Test
                </h2>
                <button
                    onClick={requestTest}
                    disabled={status === 'running' || pendingTrigger}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-brand hover:bg-brand-400 text-white text-sm transition disabled:opacity-40 disabled:cursor-not-allowed"
                >
                    <FiRefreshCw className={status === 'running' ? 'animate-spin' : ''} />
                    {status === 'running' ? 'Running…' : 'Run speed test'}
                </button>
            </header>

            <SpeedTestConfirmModal
                isOpen={confirmOpen}
                onConfirm={runTest}
                onCancel={() => setConfirmOpen(false)}
            />

            {/* Empty state */}
            {!result && <EmptyState />}

            {/* Running */}
            {status === 'running' && (
                <RunningState elapsedMs={elapsedMs} isStuck={isStuck} />
            )}

            {/* Success */}
            {status === 'success' && <SuccessResult result={result} />}

            {/* Error */}
            {status === 'error' && <ErrorState result={result} />}

            {/* Busy — really only a flash; show as info */}
            {status === 'busy' && (
                <div className="rounded-lg bg-yellow-500/10 border border-yellow-500/30 px-4 py-3 text-sm text-yellow-300 inline-flex items-center gap-2">
                    <FiAlertCircle /> A speed test is already running on this device.
                </div>
            )}
        </section>
    );
}

function EmptyState() {
    return (
        <div className="rounded-lg border border-dashed border-white/[0.08] p-6 text-center text-gray-400">
            <FiActivity className="mx-auto text-2xl mb-2 text-gray-500" />
            <p className="text-sm">No test run yet for this device.</p>
            <p className="text-xs text-gray-500 mt-1">
                Pressing <span className="font-medium">Run speed test</span> will use
                approximately 20 MB of the customer's data.
            </p>
        </div>
    );
}

function RunningState({ elapsedMs, isStuck }) {
    const seconds = Math.floor(elapsedMs / 1000);
    return (
        <div className={`rounded-lg p-5 border ${isStuck ? 'border-yellow-500/40 bg-yellow-500/5' : 'border-brand/30 bg-brand/5'}`}>
            <div className="flex items-center gap-3">
                <FiRefreshCw className="text-brand-400 animate-spin text-xl" />
                <div>
                    <p className="text-sm font-medium text-brand-400">Testing…</p>
                    <p className="text-xs text-gray-400">elapsed {seconds}s</p>
                </div>
            </div>
            {isStuck && (
                <p className="mt-3 text-xs text-yellow-300 inline-flex items-center gap-1.5">
                    <FiAlertTriangle /> Test has been running over a minute. The device
                    may have crashed mid-test — try again.
                </p>
            )}
        </div>
    );
}

function SuccessResult({ result }) {
    // Render -1 sentinel as "—" to match the doc's "value not measured" semantics.
    const fmt = (n, suffix) => (n == null || n < 0 ? '—' : `${n}${suffix}`);
    const testedAge = result.tested_at ? humanizeAge(Date.now() - Number(result.tested_at)) : null;

    return (
        <div>
            <div className="grid grid-cols-3 gap-3">
                <BigStat icon={<FiArrowDown />} label="Download" value={fmt(result.download_mbps, ' Mbps')} accent="text-green-400" />
                <BigStat icon={<FiArrowUp />} label="Upload" value={fmt(result.upload_mbps, ' Mbps')} accent="text-blue-400" />
                <BigStat icon={<FiClock />} label="Latency" value={fmt(result.latency_ms, ' ms')} accent="text-brand-400" />
            </div>
            <p className="mt-3 pt-3 border-t border-white/[0.06] text-xs text-gray-500 flex items-center justify-between">
                <span>{testedAge ? `Tested ${testedAge}` : ''}</span>
                {result.data_used_mb != null && (
                    <span><FiDownload className="inline mr-1" />used {result.data_used_mb} MB</span>
                )}
            </p>
        </div>
    );
}

function BigStat({ icon, label, value, accent }) {
    return (
        <div className="bg-surface-inset border border-white/[0.06] rounded-lg p-3">
            <div className="text-xs text-gray-400 inline-flex items-center gap-1.5">
                <span className={accent}>{icon}</span> {label}
            </div>
            <div className={`mt-1 text-lg font-semibold ${accent}`}>{value}</div>
        </div>
    );
}

function ErrorState({ result }) {
    return (
        <div className="rounded-lg bg-red-500/10 border border-red-500/30 px-4 py-3">
            <p className="text-sm text-red-300 inline-flex items-center gap-2">
                <FiAlertTriangle /> Speed test failed
            </p>
            {result.reason && (
                <p className="mt-1 text-xs text-red-300/80 break-all">{result.reason}</p>
            )}
        </div>
    );
}

// In-app confirmation for the 20 MB customer-data warning. Replaces the
// browser-native confirm() so the dialog matches the dashboard's look.
// Esc cancels; clicking the backdrop also cancels.
function SpeedTestConfirmModal({ isOpen, onConfirm, onCancel }) {
    useEffect(() => {
        if (!isOpen) return undefined;
        const onKey = (e) => { if (e.key === 'Escape') onCancel(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [isOpen, onCancel]);

    if (!isOpen) return null;

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
            onClick={onCancel}
        >
            <div
                onClick={(e) => e.stopPropagation()}
                role="dialog"
                aria-modal="true"
                aria-labelledby="speedtest-confirm-title"
                className="w-full max-w-md card-chrome shadow-2xl"
            >
                <header className="flex items-start justify-between px-5 py-4 border-b border-white/[0.06]">
                    <h3
                        id="speedtest-confirm-title"
                        className="text-base font-semibold text-white inline-flex items-center gap-2"
                    >
                        <FiZap className="text-brand-400" />
                        Run speed test on this device?
                    </h3>
                    <button
                        onClick={onCancel}
                        className="text-gray-500 hover:text-gray-200 -mr-1 -mt-1 p-1 rounded transition"
                        aria-label="Close"
                    >
                        <FiX />
                    </button>
                </header>

                <div className="px-5 py-4">
                    <ul className="space-y-2 text-sm text-gray-300">
                        <li className="flex items-start gap-2">
                            <span className="text-brand-400 mt-0.5">•</span>
                            Uses ~20 MB of the customer's data (10 MB down + 10 MB up).
                        </li>
                        <li className="flex items-start gap-2">
                            <span className="text-brand-400 mt-0.5">•</span>
                            Takes 10–30 seconds.
                        </li>
                        <li className="flex items-start gap-2">
                            <span className="text-brand-400 mt-0.5">•</span>
                            Result overwrites the previous one — no history kept.
                        </li>
                    </ul>
                </div>

                <footer className="px-5 py-4 border-t border-white/[0.06] flex justify-end gap-2">
                    <button
                        onClick={onCancel}
                        className="px-4 py-1.5 rounded-lg bg-white/[0.05] hover:bg-white/[0.08] text-sm text-gray-100 transition"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={onConfirm}
                        autoFocus
                        className="px-4 py-1.5 rounded-lg bg-brand hover:bg-brand-400 text-sm font-medium text-white transition inline-flex items-center gap-1.5"
                    >
                        <FiZap /> Run test
                    </button>
                </footer>
            </div>
        </div>
    );
}

